import {
    createFileRoute,
    Link,
    Outlet,
    redirect,
    useLocation,
    useRouter,
} from '@tanstack/react-router';
import { LogOut, PanelLeftOpen, Search } from 'lucide-react';
import { useState } from 'react';

import { AdminCommandPalette } from '@apps/web/components/admin/admin_command_palette';
import { AdminModeToggle } from '@apps/web/components/admin/admin_mode_toggle';
import {
    CORE_ITEMS,
    ENGINEERING_ITEMS,
    filterSidebarItems,
    OPS_ITEMS,
    SALES_ITEMS,
    type SidebarItem,
} from '@apps/web/components/admin/admin_sidebar_items';
import { PhoneAppBar } from '@apps/web/components/layout/phone_app_bar';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
    SidebarRail,
    SidebarTrigger,
    useSidebar,
} from '@apps/web/components/ui/sidebar';
import { useDashboardTracking } from '@apps/web/features/dashboard_usage/use_dashboard_tracking';
import { useLeaderKey } from '@apps/web/hooks/use_leader_key';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { cn } from '@apps/web/lib/utils';

// The only admin page the contractor role may open. Contractors are
// redirected here from every other /admin path. This gate is navigation
// UX; the real barrier is the API, where all admin endpoints except sales
// agreements require admin and return 403 for contractors.
const CONTRACTOR_ALLOWED_PATH = '/admin/sales/agreements';

export const Route = createFileRoute('/_app/admin')({
    beforeLoad: ({ context, location }) => {
        const user = context.user;
        const isContractor = user?.phoebe_role === 'contractor';
        if (!context.sessionState?.session || (!user?.admin && !isContractor)) {
            console.debug('redirecting to /, not admin', {
                session: context.sessionState?.session,
                user: context.user,
            });
            throw redirect({ to: '/' });
        }
        if (!user?.admin && location.pathname !== CONTRACTOR_ALLOWED_PATH) {
            throw redirect({ to: CONTRACTOR_ALLOWED_PATH });
        }
    },
    head: () => ({
        meta: [
            {
                title: 'Phoebe Admin',
            },
        ],
    }),
    component: AdminLayout,
});

function NavSection({
    label,
    items,
    currentPath,
    isQuiet = false,
    onNavigate,
}: {
    readonly label: string;
    readonly items: ReadonlyArray<SidebarItem>;
    readonly currentPath: string;
    readonly isQuiet?: boolean;
    readonly onNavigate?: () => void;
}) {
    const { isMobile, setOpenMobile } = useSidebar();

    return (
        <div>
            <h3
                className={cn(
                    'mb-2 text-[11px] font-semibold',
                    isQuiet
                        ? 'text-admin-agent-nav-fg'
                        : 'text-muted-foreground',
                )}
            >
                {label}
            </h3>
            <SidebarMenu className="gap-0.5">
                {items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton
                            asChild={true}
                            isActive={
                                currentPath === item.to ||
                                currentPath.startsWith(`${item.to}/`)
                            }
                            size="sm"
                            className={cn(
                                'w-full gap-2.5 px-2.5 py-1.5',
                                isQuiet
                                    ? 'text-admin-agent-nav-fg-strong hover:bg-admin-agent-nav-surface/70 hover:text-admin-agent-nav-fg-active data-[active=true]:bg-admin-agent-nav-surface data-[active=true]:text-admin-agent-nav-fg-active [&>svg]:text-admin-agent-nav-fg data-[active=true]:[&>svg]:text-admin-agent-nav-fg-active'
                                    : 'text-foreground/80 hover:bg-accent hover:text-foreground data-[active=true]:bg-accent data-[active=true]:text-foreground [&>svg]:text-muted-foreground data-[active=true]:[&>svg]:text-foreground',
                            )}
                        >
                            <Link
                                to={item.to}
                                onClick={() => {
                                    onNavigate?.();
                                    if (isMobile) {
                                        setOpenMobile(false);
                                    }
                                }}
                                className="flex items-center leading-4 transition-colors"
                            >
                                <item.icon className="h-4 w-4" />
                                <span className="text-[12px] font-medium">
                                    {item.label}
                                </span>
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                ))}
            </SidebarMenu>
        </div>
    );
}

function AdminLayout() {
    const router = useRouter();

    // Vim-style "g t" jumps to the agent traces index from anywhere in admin.
    // The g-prefix leader is here so more jumps can be added later.
    useLeaderKey('g', {
        // Vim-style "g a" jumps to accounts from anywhere in admin.
        a: () => router.navigate({ to: '/admin/accounts' }),
        t: () => router.navigate({ to: '/admin/agent-traces' }),
    });

    const { user } = Route.useRouteContext();
    // Contractors get the admin chrome with only the Sales section; the
    // command palette and main-app logo link are admin-only since
    // everything they lead to requires admin.
    const isAdmin = user?.admin === true;
    const location = useLocation();
    useDashboardTracking(
        location.pathname,
        isAdmin && location.pathname.startsWith('/admin/'),
    );
    const isDev = getRunEnvironment() === 'development';
    const isAgentRoute =
        location.pathname === '/admin/agent' ||
        location.pathname.startsWith('/admin/agent/');
    const engineeringItems = ENGINEERING_ITEMS.filter(
        (item) => !item.developmentOnly || isDev,
    );
    const [navQuery, setNavQuery] = useState('');

    // The nav-filter bar mirrors the settings sidebar search: as the admin
    // types, keep only the sections with a label match and hide the rest.
    // Contractors only ever see the single Sales tab, so the bar is
    // admin-only.
    const navSections = isAdmin
        ? [
              { label: 'Core', items: CORE_ITEMS },
              { label: 'Engineering', items: engineeringItems },
              { label: 'Ops', items: OPS_ITEMS },
              { label: 'Sales', items: SALES_ITEMS },
          ]
        : [{ label: 'Sales', items: SALES_ITEMS }];
    const filteredNavSections = navSections
        .map((section) => ({
            label: section.label,
            items: filterSidebarItems(section.items, navQuery),
        }))
        .filter((section) => section.items.length > 0);

    return (
        <SidebarProvider className="admin-theme">
            {isAdmin && <AdminCommandPalette />}
            <Sidebar
                collapsible="offcanvas"
                className={
                    isAgentRoute
                        ? 'bg-admin-agent-nav-surface/50'
                        : 'bg-muted/50'
                }
            >
                <SidebarContent className="px-3 py-4">
                    <SidebarHeader className="flex flex-row items-center gap-2 pb-3">
                        <Link
                            to={isAdmin ? '/' : CONTRACTOR_ALLOWED_PATH}
                            className={cn(
                                'flex items-center gap-2 rounded-md p-1 transition-colors',
                                isAgentRoute
                                    ? 'hover:bg-admin-agent-nav-surface/75'
                                    : 'hover:bg-accent',
                            )}
                            aria-label={isAdmin ? 'Back to main app' : 'Phoebe'}
                        >
                            <img
                                src="/phoebe.svg"
                                alt="Phoebe"
                                className="h-[25px] w-auto"
                            />
                            <h2
                                className={cn(
                                    'text-[14px] leading-4 font-semibold',
                                    isAgentRoute
                                        ? 'text-admin-agent-nav-fg-active'
                                        : 'text-foreground',
                                )}
                            >
                                Phoebe
                            </h2>
                        </Link>
                        <div className="flex-1" />
                        {isDev && (
                            <Badge
                                variant="secondary"
                                className={cn(
                                    'px-1.5 py-0 text-[10px]',
                                    isDev &&
                                        'bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-950/50 dark:text-yellow-300 dark:hover:bg-yellow-950/50',
                                    !isDev &&
                                        'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-950/50 dark:text-green-300 dark:hover:bg-green-950/50',
                                )}
                            >
                                DEV
                            </Badge>
                        )}
                        <SidebarTrigger />
                    </SidebarHeader>
                    {isAdmin && (
                        <div className="relative mb-4">
                            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" />
                            <Input
                                type="search"
                                value={navQuery}
                                onChange={(event) =>
                                    setNavQuery(event.target.value)
                                }
                                placeholder="Search admin…"
                                aria-label="Search admin pages"
                                className="bg-background h-8 pr-2 pl-8 text-[12px] shadow-none"
                            />
                        </div>
                    )}
                    <div className="space-y-5">
                        {filteredNavSections.length === 0 ? (
                            <p className="text-muted-foreground px-2.5 py-1.5 text-[12px]">
                                No pages found.
                            </p>
                        ) : (
                            filteredNavSections.map((section) => (
                                <NavSection
                                    key={section.label}
                                    label={section.label}
                                    items={section.items}
                                    currentPath={location.pathname}
                                    isQuiet={isAgentRoute}
                                    onNavigate={() => setNavQuery('')}
                                />
                            ))
                        )}
                    </div>
                </SidebarContent>
                <SidebarFooter
                    className={cn(
                        'gap-2 border-t px-3 py-3',
                        isAgentRoute && 'border-admin-agent-nav-border',
                    )}
                >
                    <AdminModeToggle />
                    <Button
                        variant="ghost"
                        aria-label="Sign out"
                        onClick={() => router.navigate({ to: '/sign-out' })}
                        className={cn(
                            'w-full justify-start text-[12px]',
                            isAgentRoute
                                ? 'text-admin-agent-nav-fg-strong hover:bg-admin-agent-nav-surface/75 hover:text-admin-agent-nav-fg-active'
                                : 'hover:bg-accent',
                        )}
                    >
                        <LogOut
                            className={cn(
                                'mr-2 h-4 w-4',
                                isAgentRoute
                                    ? 'text-admin-agent-nav-fg'
                                    : 'text-muted-foreground',
                            )}
                        />
                        Sign out
                    </Button>
                </SidebarFooter>
                <SidebarRail />
            </Sidebar>
            <AdminSidebarExpandButton />
            {/*
              h-svh bounds the content column to the viewport: the
              sidebar wrapper is only min-h-svh, so without an
              explicit height the column grows with page content and
              pushes the dock footer below the fold instead of
              scrolling the outlet area.
            */}
            <div className="flex h-svh min-h-0 min-w-0 flex-1 flex-col">
                <AdminMobileHeader />
                <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
                    <Outlet />
                </div>
            </div>
        </SidebarProvider>
    );
}

function AdminMobileHeader() {
    const location = useLocation();
    const isAgentRoute =
        location.pathname === '/admin/agent' ||
        location.pathname.startsWith('/admin/agent/');

    return (
        <PhoneAppBar
            navigationLabel="Open admin navigation"
            className={cn(
                'z-40',
                isAgentRoute &&
                    'border-admin-agent-nav-border/70 bg-admin-agent-nav-surface/95',
            )}
            menuButtonClassName={
                isAgentRoute ? 'text-admin-agent-nav-fg-active' : undefined
            }
            title={
                <span
                    className={cn(
                        'text-sm',
                        isAgentRoute
                            ? 'text-admin-agent-nav-fg-active'
                            : 'text-foreground',
                    )}
                >
                    Phoebe Admin
                </span>
            }
            trailing={
                <div className="hidden w-44 sm:block">
                    <AdminModeToggle />
                </div>
            }
        />
    );
}

function AdminSidebarExpandButton() {
    const { isMobile, openMobile, state, toggleSidebar } = useSidebar();
    const shouldShow = !isMobile && !openMobile && state === 'collapsed';

    if (!shouldShow) {
        return null;
    }

    return (
        <Button
            variant="outline"
            size="icon"
            aria-label="Expand sidebar"
            onClick={toggleSidebar}
            className="bg-background fixed top-3 left-3 z-50 h-8 w-8 shadow-sm"
        >
            <PanelLeftOpen className="h-4 w-4" />
        </Button>
    );
}
